import axios from "axios";
import { useMediaQuery } from "@mui/material";
import Swal from "sweetalert2";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const formattedToken = `Bearer ${token.replace(/"/g, "")}`;
  return {
    Authorization: formattedToken,
    "Content-Type": "application/json",
  };
};

export const fetchJsonDynamic = async (
  objectName = null,
  inputObjectName = null,
  params = null,
  baseUrl
) => {
  const headers = getAuthHeaders();

  // Construct the query dynamically based on the presence of params
  const query = {
    query: `
      {
        ${objectName}${params
        ? `(${inputObjectName}: {${Object.entries(params)
          .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
          .join(", ")}})`
        : ""
      } {
            results
            error
        }
      }
    `,
  };

  try {
    const response = await axios.post(baseUrl, query, { headers });
    return response?.data?.data?.[objectName];
  } catch (error) {
    return { error: "Failed to fetch data" };
  }
};
// Sibling to fetchJsonDynamic, for resolvers that take flat, top-level
// scalar arguments instead of a single wrapped input object — e.g.
// GetAllUsersView(PageNumber, PageSize, searchTerm, includeTotalCount),
// as opposed to allUsersByColumnValues(usermodel: {...}). fetchJsonDynamic
// always wraps params under `${inputObjectName}: {...}`, which produces an
// invalid `null: {...}` argument when there's no wrapper object — this
// function just emits the params as direct arguments instead.
  export const fetchJsonDynamicFlatArgs = async (
    objectName = null,
    params = null,
    baseUrl
  ) => {
    const headers = getAuthHeaders();

    const query = {
      query: `
        {
          ${objectName}${
            params
              ? `(input: {
                  ${Object.entries(params)
                    .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                    .join("\n")}
                })`
              : ""
          } {
            results
            error
          }
        }
      `,
    };

    try {
      const response = await axios.post(baseUrl, query, { headers });
      return response?.data?.data?.[objectName];
    } catch (error) {
      return { error: "Failed to fetch data" };
    }
  };

export const fetchCustomerStatementJsonDynamic = async (
  objectName = null,
  inputObjectName = null,
  params = null,
  baseUrl
) => {
  const headers = getAuthHeaders();

  // Convert params into a string of key-value pairs
  const formattedParams = Object.entries(params)
    .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
    .join(", ");

  // Construct the GraphQL query string properly
  const query = {
    query: `
      {
        ${objectName}(${formattedParams}) {
          results
          error
        }
      }
    `,
  };

  try {
    const response = await axios.post(baseUrl, query, { headers });
    return response?.data?.data?.[objectName];
  } catch (error) {
    return { error: "Failed to fetch data" };
  }
};

export const fetchJson = async ({
  objectName = null,
  inputObjectName = null,
  params = null,
  baseUrl,
}) => {
  const headers = getAuthHeaders();

  // Construct the query dynamically based on the presence of params
  const query = {
    query: `
      {
        ${objectName}${params
        ? `(${inputObjectName}: {${Object.entries(params)
          .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
          .join(", ")}})`
        : ""
      } {
          results
          error
        }
      }
    `,
  };

  try {
    const response = await axios.post(baseUrl, query, { headers });
    return response?.data?.data?.[objectName];
  } catch (error) {
    return { error: "Failed to fetch data" };
  }
};
const getRandomColumns = (columns, num) => {
  const shuffled = [...columns].sort(() => 0.5 - Math.random()); // Shuffle columns randomly
  return shuffled.slice(0, num); // Pick the first 'num' columns
};

export const generateColumnsFromResults = (
  resultsArray,
  MoreignoreFields,
  isMobile
) => {
  // Ensure there's data to process
  if (!resultsArray || resultsArray.length === 0) return [];

  // Get all keys (fields) from the first object in the results array
  const result = resultsArray[0];
  const allowedIsFields = [
    "IsGroup",
    "IsDepositRequired",
    "IsDepositPercentage",
    "IsPercentage",
    "IsApproved",
  ]; // Add any "is" fields you want to allow
  const allowedIdFields = ["TransId", "LoanId"];

  const ignoreNullCheck = MoreignoreFields ? MoreignoreFields : [];
  // Fields to ignore (including those passed in the "ignore" parameter)
  const ignoreFields = [
    "CreatedDate",
    "CreatedBy",
    "UpdatedDate",
    "UpdatedBy",
    //"isActive",
    //"CompanyName",
    ...(MoreignoreFields || []),
  ];

  // Get all keys (fields) from the result object
  const fields = Object.keys(result);

  // Map the extracted field names to the desired column format, ignoring specific fields
  let columns = fields
    .filter(
      (field) =>
        !ignoreFields.includes(field) && // Exclude predefined ignored fields
        (field.toLowerCase() === "id" ||
          !field.toLowerCase().endsWith("id") ||
          allowedIdFields.includes(field)) && // ✅ explicitly allow LoanId and TransId
        (!field.toLowerCase().startsWith("is") ||
          allowedIsFields.includes(field))
    )

    .map((field) => {
      const headerName = formatHeaderName(field === "Id" ? "id" : field);
      return {
        field: field === "Id" ? "id" : field,
        headerName,
        // Proportional flex: longer headers get more space (min 0.7 units)
        flex: Math.max(0.7, headerName.length / 10),
        minWidth: 80,
      };
    });
  if (isMobile) {
    // If mobile, pick only 3 random columns from the generated columns
    columns = getRandomColumns(columns, 2);
  }

  return columns;
};

// Helper function to format the header name (camelCase or PascalCase to readable)
const formatHeaderName = (field) => {
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());
};

// Helper function to build GraphQL mutation queries dynamically
export const generateMutationQuery = (
  mutationName,
  variables,
  mutationObjectName,
  isBulk
) => {
  let args = "";

  // Helper function to format the values based on their datatype
  const formatValue = (value) => {
    if (typeof value === "string") {
      // Enclose strings in quotes
      return `"${value}"`;
    } else if (Array.isArray(value)) {
      // If it's an array, handle each item based on its type
      return `[${value.map((item) => formatValue(item)).join(", ")}]`;
    } else if (value === null || value === undefined || Number.isNaN(value)) {
      // Handle null, undefined, or NaN values
      return `null`;
    } else {
      // Return the value as is (for numbers, booleans, etc.)
      return value;
    }
  };

  if (isBulk) {
    // Handle bulk operations (arrays of objects or IDs)
    for (const [key, value] of Object.entries(variables)) {
      args += `${key}: ${formatValue(value)}, `;
    }
  } else {
    // Handle single or object-based mutations
    for (const [key, value] of Object.entries(variables)) {
      args += `${key}: ${formatValue(value)}, `;
    }
  }

  // Remove trailing comma and space
  args = args.trim().replace(/,\s*$/, "");

  // Conditionally include mutationObjectName only if it's provided
  return mutationObjectName
    ? `
    mutation {
      ${mutationName}(${mutationObjectName}: {${args}}) {
        results
        error
      }
    }
  `
    : `
    mutation {
      ${mutationName}(${args}) {
        results
        error
      }
    }
  `;
};

// Dynamic mutation service
export const executeAnyMutation = async (
  mutationName,
  variables,
  mutationObjectName,
  isBulk,
  baseUrl
) => {
  const headers = getAuthHeaders(); // Add authentication headers
  const query = generateMutationQuery(
    mutationName,
    variables,
    mutationObjectName,
    isBulk
  ); // Generate mutation query

  try {
    const response = await axios.post(baseUrl, { query }, { headers });
    // Surface GQL-level errors (e.g. HotChocolate validation / auth errors)
    if (response?.data?.errors?.length) {
      const firstErr = response.data.errors[0];
      const msg = firstErr?.extensions?.message || firstErr?.message || "GraphQL error";
      return { error: JSON.stringify({ message: msg }) };
    }
    return response?.data?.data?.[mutationName];
  } catch (error) {
    const status = error?.response?.status;
    const gqlErrors = error?.response?.data?.errors;
    if (gqlErrors?.length) {
      const msg = gqlErrors[0]?.extensions?.message || gqlErrors[0]?.message || "GraphQL error";
      return { error: JSON.stringify({ message: msg }) };
    }
    if (status === 401) return { error: JSON.stringify({ message: "Session expired. Please log in again." }) };
    if (status === 403) return { error: JSON.stringify({ message: "You do not have permission to perform this action." }) };
    if (status) return { error: JSON.stringify({ message: `Server error (HTTP ${status}). Please try again.` }) };
    return { error: JSON.stringify({ message: error?.message || "Network error. Check your connection." }) };
  }
};

export const parseErrorMessage = (rawError, entityName = "Record") => {
  if (!rawError) return "An unexpected error occurred. Please try again.";

  let errorStr = "";

  // 1. Unwrap object or JSON string
  if (typeof rawError === "object") {
    // Handle ASP.NET ValidationProblemDetails format: { errors: { field: ["msg1", "msg2"] } }
    if (rawError.errors && typeof rawError.errors === "object") {
      const validationMsgs = Object.entries(rawError.errors)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
        .join("\n");
      if (validationMsgs) return validationMsgs;
    }
    errorStr =
      rawError.message ||
      rawError.Error ||
      rawError.error ||
      rawError.Message ||
      rawError.detail ||
      JSON.stringify(rawError);
  } else if (typeof rawError === "string") {
    try {
      const parsed = JSON.parse(rawError);
      if (parsed && typeof parsed === "object") {
        return parseErrorMessage(parsed, entityName);
      }
      errorStr = String(parsed);
    } catch {
      errorStr = rawError;
    }
  } else {
    errorStr = String(rawError);
  }

  const lower = errorStr.toLowerCase();

  // 2. Check for Postgres / SQL Database Constraints
  // A. Unique constraint violation (Postgres 23505 / duplicate key)
  if (
    lower.includes("23505") ||
    lower.includes("duplicate key") ||
    lower.includes("unique constraint")
  ) {
    // Extract constraint name (e.g. "idx_carmake_code", "idx_34767_pk__loansettings", "idx_carmake_makename")
    const constraintMatch =
      errorStr.match(/constraint\s*["']?([^"'\s,]+)["']?/i) ||
      errorStr.match(/key\s*\(([^)]+)\)=/i);

    let fieldHint = "";
    if (constraintMatch && constraintMatch[1]) {
      const rawName = constraintMatch[1]
        .replace(/^(idx_|pk_|fk_|uq_)/i, "")
        .replace(/_(key|unique)$/i, "");
      const parts = rawName.split("_");
      const cleanField = parts.length > 1 ? parts[parts.length - 1] : rawName;
      if (cleanField && cleanField.length < 30) {
        fieldHint = ` with this ${cleanField.charAt(0).toUpperCase() + cleanField.slice(1)}`;
      }
    }
    return `A ${entityName}${fieldHint} already exists. Please enter a different value.`;
  }

  // B. Foreign key constraint violation (Postgres 23503 / foreign key)
  if (lower.includes("23503") || lower.includes("foreign key")) {
    return `The selected reference record does not exist or is linked to other existing records.`;
  }

  // C. Not null constraint violation (Postgres 23502 / null value)
  if (
    lower.includes("23502") ||
    lower.includes("violates not-null") ||
    lower.includes("cannot be null")
  ) {
    const colMatch = errorStr.match(/column\s*["']?([^"'\s]+)["']?/i);
    const colHint = colMatch && colMatch[1] ? ` (${colMatch[1]})` : "";
    return `A required field${colHint} was not provided. Please check all required fields and try again.`;
  }

  // D. Check constraint violation (Postgres 23514 / check constraint)
  if (lower.includes("23514") || lower.includes("check constraint")) {
    return `One or more values violate the allowed validation range or criteria.`;
  }

  // E. Strip .NET Stack Trace / Npgsql / Dapper internal traces
  if (
    errorStr.includes("at ") ||
    errorStr.includes("Npgsql.") ||
    errorStr.includes("System.Runtime") ||
    errorStr.includes("Dapper.")
  ) {
    // If MessageText is present (from PostgresException / Exception Data)
    const messageTextMatch = errorStr.match(/MessageText:\s*([^\n\r]+)/i);
    if (messageTextMatch && messageTextMatch[1]) {
      const msg = messageTextMatch[1].trim();
      return parseErrorMessage(msg, entityName);
    }

    // Extract first sentence before 'DETAIL:' or 'at System' or 'at Npgsql'
    const firstLine = errorStr.split(/\r?\n|DETAIL:|at\s+[A-Za-z0-9_.]+\(/)[0].trim();
    if (
      firstLine &&
      firstLine.length < 150 &&
      !firstLine.toLowerCase().includes("exception")
    ) {
      return firstLine;
    }

    return "An unexpected server error occurred. Please contact the administrator.";
  }

  // 3. Clean business validation error (return trimmed message)
  if (errorStr.length > 250) {
    return errorStr.substring(0, 250) + "...";
  }

  return errorStr.trim() || "An unexpected error occurred.";
};

/**
 * Dynamic form submit handler:
 * - Automatically checks response for errors (GraphQL/backend errors)
 * - On error: formats error cleanly, shows Swal error dialog in front of drawer, keeps all inputs intact
 * - On success: calls onSuccess callback (closes modal and refreshes data) and shows Swal success dialog
 */
export const executeDynamicSubmit = async ({
  mutationName,
  variables,
  mutationObjectName,
  isBulk = false,
  baseUrl,
  isEditing = false,
  entityName = "Record",
  successMessage,
  onSuccess,
  setSubmitting,
}) => {
  try {
    const response = await executeAnyMutation(
      mutationName,
      variables,
      mutationObjectName,
      isBulk,
      baseUrl
    );

    // Check for GraphQL / backend error inside the response
    if (response?.error) {
      const errorMessage = parseErrorMessage(response.error, entityName);
      await Swal.fire({
        icon: "error",
        title: "Validation / Server Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
      });
      return { success: false, error: errorMessage };
    }

    if (response) {
      if (onSuccess) {
        onSuccess(response);
      }
      const msg =
        successMessage ||
        `${entityName} has been ${
          isEditing ? "updated" : "created"
        } successfully`;
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: msg,
        confirmButtonColor: "#3085d6",
      });
      return { success: true, data: response?.results };
    }

    await Swal.fire({
      icon: "error",
      title: "Error",
      text: "No response received from server.",
      confirmButtonColor: "#d33",
    });
    return { success: false, error: "No response" };
  } catch (err) {
    const message =
      err.message || "An unexpected error occurred while processing your request.";
    await Swal.fire({
      icon: "error",
      title: "Error",
      text: message,
      confirmButtonColor: "#d33",
    });
    return { success: false, error: message };
  } finally {
    if (setSubmitting) {
      setSubmitting(false);
    }
  }
};

